import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Banner, Button, LoadingScreen } from '@cowoo/shared/ui-web';
import { ApiProblem, formatDate, formatMoney, newIdempotencyKey, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { Field, Page, fieldClass, IconTile } from '../../ui';
import { grossOf } from './Contracts';

/** § 14 — `included_seats` is 1–6, and never more than the room holds. */
const MAX_SEATS = 6;

/**
 * `/vertraege/neu` (10-web-admin § 14, BR-CON-09).
 *
 * Offices are not sold self-service — there is no funnel for them, and this
 * form is the only way one is let. It creates the billing account and the team
 * behind it in the same request, because an office without an account to
 * invoice is not a contract.
 *
 * The summary column restates what is about to be created rather than
 * repeating the inputs: staff read it, not the form, before pressing the
 * button that starts charging a company every month.
 */
export function ContractNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [resourceId, setResourceId] = useState('');
  const [company, setCompany] = useState('');
  const [vatId, setVatId] = useState('');
  const [street, setStreet] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [monthly, setMonthly] = useState('');
  const [seats, setSeats] = useState('1');
  const [startDay, setStartDay] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<ApiProblem | null>(null);

  const offices = useQuery({
    queryKey: qk.admin.area('offices'),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/offices');
      if (error) throw error;
      return data.items;
    },
  });

  const office = offices.data?.find((item) => item.resourceId === resourceId);
  const netCents = Math.round(Number(monthly.replace(',', '.')) * 100);
  const seatCount = Number(seats);

  /**
   * The seat cap is the room's capacity, and the server checks it too.
   *
   * Shown here so the number cannot be typed in the first place, refused there
   * because the office could have been let in the meantime.
   */
  const maxSeats = Math.min(MAX_SEATS, office?.capacity ?? MAX_SEATS);

  const valid =
    resourceId !== '' &&
    company.trim() !== '' &&
    street.trim() !== '' &&
    postalCode.trim() !== '' &&
    city.trim() !== '' &&
    Number.isInteger(netCents) &&
    netCents > 0 &&
    seatCount >= 1 &&
    seatCount <= maxSeats &&
    /^\d{4}-\d{2}-\d{2}$/.test(startDay);

  const create = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.POST('/admin/contracts/office', {
        params: { header: { 'idempotency-key': newIdempotencyKey() } },
        body: {
          resourceId,
          company: {
            name: company.trim(),
            vatId: vatId.trim() === '' ? null : vatId.trim(),
            street: street.trim(),
            postalCode: postalCode.trim(),
            city: city.trim(),
            country: 'DE',
          },
          customNetCents: netCents,
          includedSeats: seatCount,
          startDay,
          ...(note.trim() === '' ? {} : { note: note.trim() }),
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: qk.admin.area('contracts') });
      void navigate(`/vertraege/${data.id}`);
    },
    onError: (error) => setProblem(error instanceof ApiProblem ? error : null),
  });

  if (offices.isLoading) return <LoadingScreen label={t('common.loading')} />;

  return (
    <Page>
      <section className="border-line rounded-card flex min-w-0 flex-1 flex-col gap-4 border bg-white px-6 py-5">
        <div className="flex items-center gap-3.5">
          {/* The board's way back to the list: a tile, not a text link. */}
          <span className="bg-fog rounded-input flex h-9 w-9 flex-none items-center justify-center">
            <IconTile
              icon="back"
              label={t('common.back')}
              size={36}
              onClick={() => void navigate('/vertraege')}
            />
          </span>
          <div>
            <h1 className="text-webH3 text-ink">{t('admin.contractNew.title')}</h1>
            <p className="text-adminBody text-muted">{t('admin.contractNew.intro')}</p>
          </div>
        </div>

        {problem ? <Banner tone="danger">{t(problem.i18nKey)}</Banner> : null}

        <Field label={t('admin.contractNew.office')} hint={t('admin.contractNew.officeHint')}>
          <select
            value={resourceId}
            onChange={(event) => setResourceId(event.target.value)}
            className={fieldClass}
          >
            <option value="">{t('admin.contractNew.officePlaceholder')}</option>
            {(offices.data ?? []).map((item) => (
              <option key={item.resourceId} value={item.resourceId}>
                {[item.label, item.locationName, t('admin.contractNew.upTo', { n: item.capacity ?? 0 })]
                  .filter(Boolean)
                  .join(' · ')}
              </option>
            ))}
          </select>
        </Field>

        <p className="text-label text-muted pt-1 uppercase">{t('admin.contractNew.account')}</p>
        <div className="grid grid-cols-2 gap-3.5">
          <Field label={t('admin.contractNew.company')}>
            <input value={company} onChange={(e) => setCompany(e.target.value)} className={fieldClass} />
          </Field>
          <Field label={t('admin.contractNew.vatId')}>
            <input value={vatId} onChange={(e) => setVatId(e.target.value)} className={fieldClass} />
          </Field>
          <Field label={t('admin.contractNew.street')}>
            <input value={street} onChange={(e) => setStreet(e.target.value)} className={fieldClass} />
          </Field>
          <div className="flex gap-3.5">
            <div className="w-[120px]">
              <Field label={t('admin.contractNew.postalCode')}>
                <input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className={fieldClass}
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field label={t('admin.contractNew.city')}>
                <input value={city} onChange={(e) => setCity(e.target.value)} className={fieldClass} />
              </Field>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3.5">
          <Field
            label={t('admin.contractNew.monthlyNet')}
            hint={
              netCents > 0
                ? t('admin.contracts.grossInclVat', { amount: formatMoney(grossOf(netCents)) })
                : undefined
            }
          >
            <input
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              inputMode="decimal"
              className={fieldClass}
            />
          </Field>
          <Field label={t('admin.contractNew.seats')} hint={t('admin.contractNew.seatsHint')}>
            <input
              type="number"
              min={1}
              max={maxSeats}
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              className={fieldClass}
            />
          </Field>
          <Field label={t('admin.contractNew.start')}>
            <input
              type="date"
              value={startDay}
              onChange={(e) => setStartDay(e.target.value)}
              className={fieldClass}
            />
          </Field>
        </div>

        <Field label={t('admin.contractNew.note')}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('admin.contractNew.notePlaceholder')}
            className="border-line-strong rounded-input text-adminBody h-[72px] border px-3 py-2"
          />
        </Field>
      </section>

      <aside className="border-line rounded-card flex w-[340px] flex-none flex-col gap-3 border bg-white px-5 py-[18px]">
        <p className="text-label text-muted uppercase">{t('admin.contractNew.summary')}</p>
        <Summary label={t('admin.contractNew.office')} value={office ? office.label : '—'} />
        <Summary label={t('admin.contractNew.account')} value={company.trim() || '—'} />
        <Summary
          label={t('admin.contractNew.summaryMonthlyNet')}
          value={netCents > 0 ? formatMoney(netCents) : '—'}
        />
        <Summary
          label={t('admin.contractNew.monthlyGross')}
          value={netCents > 0 ? formatMoney(grossOf(netCents)) : '—'}
        />
        <Summary label={t('admin.contractNew.seats')} value={seats} />
        <Summary
          label={t('admin.contractNew.start')}
          value={/^\d{4}-\d{2}-\d{2}$/.test(startDay) ? formatDate(startDay) : '—'}
        />

        {/* BR-CON-03 — a contract that does not start on the 1st is billed
            pro rata. The amount is not computed here: the server owns the
            proration and a second implementation would eventually disagree
            with it (OI-F-22). */}
        {startDay.endsWith('-01') ? null : (
          <p className="text-adminSmall text-muted leading-normal">{t('admin.contractNew.proRata')}</p>
        )}

        <div className="mt-auto flex flex-col gap-2">
          <Button size="sm" disabled={!valid} loading={create.isPending} onClick={() => create.mutate()}>
            {t('admin.contractNew.submit')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => void navigate('/vertraege')}>
            {t('common.cancel')}
          </Button>
        </div>
      </aside>
    </Page>
  );
}

function Summary({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-hairline text-adminBody flex justify-between gap-3 border-b pb-1.5">
      <span className="text-muted">{label}</span>
      <span className="text-ink text-right font-semibold">{value}</span>
    </div>
  );
}
